import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';

import { UI, type UiBadgeVariant } from '../../components/ui';
import { ApiService } from '@mm-aggregator/drf-dynamics';
import {
  isAmountCurrencyCell,
  isChoiceEnumCell,
  isCompositeCell,
  isStackedCell,
  type ChoiceEnumCell,
} from '@mm-aggregator/drf-dynamics';
import type { EntityConfig } from '../../config/entity-config';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { goBackOrNavigate } from '../../utils/navigation';

/**
 * Default detail page: fetches one object by ID and displays key-value pairs
 * with smart formatting for booleans, dates, numbers, IDs, and JSON sub-objects.
 */
@Component({
  selector: 'app-entity-detail-page',
  standalone: true,
  imports: [CommonModule, RouterLink, ...UI],
  templateUrl: './entity-detail-page.component.html',
})
export class EntityDetailPageComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;
  @Input() id!: string;

  private readonly api = inject(ApiService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly shellRoutes = inject(ShellRoutesService);

  readonly data = signal<Record<string, unknown> | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly copyToast = signal(false);
  private copyToastTimeout: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    if (!this.entityConfig?.listPath || !this.id) {
      this.loading.set(false);
      this.error.set('Missing entity or id.');
      return;
    }
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    const path = `${this.entityConfig.listPath.replace(/\/$/, '')}/${this.id}/`;
    this.api.get<Record<string, unknown>>(path).subscribe({
      next: (obj) => {
        this.data.set(obj);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message ?? 'Failed to load.');
        this.loading.set(false);
      },
    });
  }

  get entityKey(): string {
    return this.entityConfig?.key ?? '';
  }

  get editLink(): (string | number | boolean)[] {
    return this.shellRoutes.commands('manage', this.entityKey, this.id, 'edit');
  }

  goBack(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', this.entityKey));
  }

  objectKeys(obj: Record<string, unknown>): string[] {
    return Object.keys(obj);
  }

  formatKey(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (s) => s.toUpperCase());
  }

  /* ─── Value type detection ─── */

  isBoolean(value: unknown): boolean {
    return typeof value === 'boolean';
  }

  isDate(value: unknown, key: string): boolean {
    if (typeof value !== 'string') return false;
    const lc = key.toLowerCase();
    if (lc.endsWith('_at') || lc.endsWith('_date') || lc === 'date' || lc === 'timestamp'
        || lc === 'created' || lc === 'updated') return true;
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value);
  }

  isId(value: unknown, key: string): boolean {
    if (typeof value !== 'string') return false;
    const lc = key.toLowerCase();
    if (lc === 'id' || lc.endsWith('_id') || lc === 'uuid' || lc === 'pk') return true;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }

  isNumber(value: unknown, key: string): boolean {
    if (typeof value === 'number') return true;
    if (typeof value !== 'string') return false;
    const lc = key.toLowerCase();
    const numericCols = ['amount', 'balance', 'price', 'total', 'count', 'quantity', 'priority', 'route_count'];
    const isNumCol = numericCols.some((c) => lc === c || lc.endsWith('_' + c));
    return isNumCol && /^-?\d+(\.\d+)?$/.test(value.trim());
  }

  isSubObject(value: unknown): boolean {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  isArray(value: unknown): boolean {
    return Array.isArray(value);
  }

  isChoiceEnum(value: unknown): value is ChoiceEnumCell {
    return isChoiceEnumCell(value);
  }

  isCompositeCell(value: unknown): boolean {
    return isCompositeCell(value);
  }

  isStackedCell(value: unknown): boolean {
    return isStackedCell(value);
  }

  isAmountCurrencyCell(value: unknown): boolean {
    return isAmountCurrencyCell(value);
  }

  choiceBadgeVariant(value: unknown): UiBadgeVariant {
    if (!this.isChoiceEnum(value)) {
      return 'neutral';
    }
    const css = String(value.css ?? 'default').toLowerCase().trim();
    const map: Record<string, UiBadgeVariant> = {
      success: 'success',
      danger: 'danger',
      warning: 'warning',
      info: 'brand',
      primary: 'brand',
    };
    return map[css] ?? 'neutral';
  }

  choiceTitle(value: unknown): string {
    if (!this.isChoiceEnum(value)) {
      return this.formatValue(value);
    }
    return String(value.title ?? value.label ?? value.value ?? '—');
  }

  isWalletStatementDepositLink(key: string, obj: Record<string, unknown>): boolean {
    if (this.entityKey !== 'wallet-statements') {
      return false;
    }
    return key === 'transaction_object_id' && String(obj['transaction_type'] ?? '').toUpperCase() === 'DEPOSIT';
  }

  walletStatementDepositLink(obj: Record<string, unknown>): (string | number | boolean)[] {
    return this.shellRoutes.commands('manage', 'deposits', String(obj['transaction_object_id'] ?? ''));
  }

  /* ─── Formatters ─── */

  formatDate(value: string): string {
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
      }
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
        + ' at ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return value;
    }
  }

  formatNumber(value: unknown): string {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) return String(value);
    if (Number.isInteger(num)) return num.toLocaleString();
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  formatValue(value: unknown): string {
    if (value == null) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  subObjectKeys(value: unknown): string[] {
    if (typeof value === 'object' && value !== null) {
      return Object.keys(value as Record<string, unknown>);
    }
    return [];
  }

  subObjectValue(obj: unknown, key: string): unknown {
    return (obj as Record<string, unknown>)?.[key];
  }

  formatSubValue(value: unknown): string {
    if (value == null) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  /* ─── Copy ─── */

  copyToClipboard(value: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {
      if (this.copyToastTimeout) clearTimeout(this.copyToastTimeout);
      this.copyToast.set(true);
      this.copyToastTimeout = setTimeout(() => this.copyToast.set(false), 1500);
    });
  }
}
